import React, { FC, useState } from 'react';

import { Grid, Card, CardHeader, CardContent, makeStyles } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import { findCurrentPeriod } from './timelineHelpers';
import Periods from './Periods';
import Player from './Player';
import VideoAssetsList from './VideoAssetsList';

import SimpleDateInfo from '../common/SimpleDateInfo';
import SimpleTextInfo from '../common/SimpleTextInfo';

import { useDrmMediaUrls } from '../../hooks';
import { DRMTYPE, Highlight, HLS_TRANSPORT, Period, VideoAsset, Status } from '../../utils/types';
import { VideoPlayerMulti } from '../common/VideoPlayer';
import { useFeatures } from 'flagged';

dayjs.extend(duration);

const useStyles = makeStyles((theme) => ({
	detailsGridItem: {
		marginBottom: theme.spacing(2)
	},
	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	highlightDetailsCard: ({ height }: StyleProps) => ({
		marginBottom: theme.spacing(2),
		height
	}),
	playerGridItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: theme.spacing(2)
	}
}));

type StyleProps = { height: string | number };

export const HighlightDetails: FC<HighlightDetailsProps> = (props) => {
	const {
		availableTime,
		drm = false,
		editorialPeriodPlayIds,
		game,
		gameId,
		height,
		hidePeriodDetails,
		id,
		onPeriodRowDelete,
		onPeriodRowPick,
		onPeriodRowUpdate,
		periods,
		publishTime,
		sources,
		sport,
		title = 'Highlight details',
		type,
		videoAssets
	} = props;

	const { defaultPackagingConfiguration: defaultPackagingConfigurationFlag } = useFeatures();
	const defaultPackagingConfiguration = defaultPackagingConfigurationFlag.toString();

	let pCarrier = 0;
	const videoPeriods = !periods?.items
		? []
		: periods.items
				.filter((period): boolean => period.type !== 'Text')
				.map((period) => {
					if (!period.startPosition || !period.endPosition) {
						period.startPosition = dayjs.duration(pCarrier, 'seconds').toISOString();
						pCarrier += dayjs(period.source.endTime!).diff(dayjs(period.source.startTime!), 's');
						period.endPosition = dayjs.duration(pCarrier, 'seconds').toISOString();
					}

					return period;
				});

	const classes = useStyles({ height: height || 'auto' });
	const [startPosition, setStartPosition] = useState(0);
	const [endPosition, setEndPosition] = useState<number | null>(null);
	const [currentPeriod, setCurrentPeriod] = useState<Period | null>((videoPeriods && videoPeriods[0]) ?? null);
	const [startPeriodIdx, setStartPeriodIdx] = useState<number>(0);

	const drmHlsSource = sources?.find(({ transport }): boolean => transport === HLS_TRANSPORT);
	const mediaDataUrls = useDrmMediaUrls({ drmHlsSource, status:game?.status as Status })
	const getAsset = (assets: VideoAsset[]): VideoAsset => {
		return assets.find((item: VideoAsset) => item.id === defaultPackagingConfiguration || assets.find((item: VideoAsset) => item.format === defaultPackagingConfiguration))!;
	};

	const getEndpoint = (period: Period): string => {
		return period.endpoints
			? period.endpoints.find((endpoint) => endpoint.format === defaultPackagingConfiguration)?.url || ''
			: period.endpoint || '';
	};

	const getMainVideoUrl = (): string | null => {
		const mainVideo = videoAssets && videoAssets.items ? getAsset(videoAssets.items) : null;
		const mainVideoUrl = mainVideo && mainVideo.url ? mainVideo.url : null;
		return mainVideoUrl;
	};

	const getVideoUrl = (periods: Period[] | []): string | null => {
		if (periods.length <= 0) return null;

		const mainVideoUrl = getMainVideoUrl();
		if (!drm && mainVideoUrl) {
			return mainVideoUrl;
		} else if (periods && periods[0]) {
			const endpointUrl = getEndpoint(periods[0]);
			if (endpointUrl) {
				return endpointUrl;
			}
			return null;
		} else {
			return null;
		}
	};

	const [videoUrl, setVideoUrl] = useState<string | null>(getVideoUrl(videoPeriods ?? []));

	const handleRowPlay = (event: unknown, data: Period): void => {
		const { endPosition = null, startPosition = 0 } = data;
		const startPositionSecs = startPosition ? dayjs.duration(startPosition).asSeconds() : 0;
		const endPositionSecs = endPosition ? dayjs.duration(endPosition).asSeconds() : null;

		const newUrl = getEndpoint(data) || null;

		if (!getMainVideoUrl() && newUrl) {
			setVideoUrl(newUrl);
			setCurrentPeriod(data);
			setStartPosition(0);
			setEndPosition(null);
		} else {
			setStartPosition(startPositionSecs);
			setEndPosition(endPositionSecs);
		}

		if (drm) {
			setStartPeriodIdx(
				videoPeriods?.findIndex((period: Period): boolean => getEndpoint(period) === newUrl) || 0
			);
		}
	};

	const handleTimeUpdate = (position: number): void => {
		if (((!drm && getMainVideoUrl()) || drm) && videoPeriods?.length) {
			const currentPeriod = findCurrentPeriod(videoPeriods, position);
			currentPeriod && setCurrentPeriod(currentPeriod);
		}
	};

	const handlePeriodUpdate = (periodIdx: number): void => {
		const currentPeriod = periods?.items?.[periodIdx];
		currentPeriod && setCurrentPeriod(currentPeriod);
	};

	const handleVideoFinished = (url: string): void => {
		const mainVideoUrl = getMainVideoUrl();
		if (!drm && mainVideoUrl === url) {
			return;
		} else {
			const currentIndex = videoPeriods?.findIndex((period: Period): boolean => getEndpoint(period) === url);
			if (currentIndex !== undefined && videoPeriods && currentIndex > -1 && currentIndex < videoPeriods?.length - 1) {
				handleRowPlay(null, videoPeriods[currentIndex + 1]);
			}
		}
	};

	const handleClearEndTime = (): void => {
		setEndPosition(null);
	};

	return (
		<>
			<Card className={classes.highlightDetailsCard}>
				<CardHeader title={title} />
				<CardContent>
					<Grid container spacing={3}>
						<Grid container item xs={12} lg={4} direction='column' spacing={2} className={classes.detailsGridItem}>
							<SimpleTextInfo label='ID' value={id} to={`/highlights/${encodeURIComponent(id)}`} />
							<SimpleDateInfo label='Available time' value={availableTime ?? ''} showInvalid='Unavailable' />
							<SimpleTextInfo label='Type' value={type || ''} />
							<SimpleTextInfo label='Game' value={game?.title || 'not found'} to={`/games/${encodeURIComponent(gameId || 'null')}`} />
							<SimpleDateInfo label='Publish time' value={publishTime ?? ''} showInvalid />
							<VideoAssetsList videoAssets={videoAssets?.items} />
						</Grid>
						{drm && drmHlsSource && mediaDataUrls.isLoading && (
							<Grid item xs={12} lg={5} spacing={1} direction='column' className={classes.playerGridItem}>
								<Alert severity='warning' style={{ width: '100%' }}>
									DRM license aquisition in progress
								</Alert>
							</Grid>
						)}
						{drm && drmHlsSource && mediaDataUrls.licenseUrl && (
							<Grid item xs={12} lg={5} spacing={1} direction='column' className={classes.playerGridItem}>
								<VideoPlayerMulti
									drmType={drmHlsSource?.protection.toLowerCase() as DRMTYPE}
									height='400px'
									laSource={mediaDataUrls.licenseUrl}
									sources={videoPeriods}
									onPeriodUpdate={handlePeriodUpdate}
									startPeriodIdx={startPeriodIdx}
								/>
								<Alert severity='info' style={{ width: '100%' }}>
									<AlertTitle>Video stream</AlertTitle>A VPN connection is required to watch clipped DRM content
								</Alert>
							</Grid>
						)}
						{!drm && videoUrl && (
							<Grid item xs={12} lg={5} className={classes.playerGridItem}>
								<Player
									clearEndTime={handleClearEndTime}
									endPosition={endPosition}
									onTimeUpdate={handleTimeUpdate}
									onVideoFinished={handleVideoFinished}
									periods={videoPeriods}
									startPosition={startPosition}
									url={videoUrl}
								/>
							</Grid>
						)}
					</Grid>
				</CardContent>
			</Card>
			<Periods
				currentPeriod={currentPeriod || undefined}
				sport={sport}
				periods={periods && periods.items ? periods.items : []}
				editorialPeriodPlayIds={editorialPeriodPlayIds ?? []}
				gameTitle={game?.title}
				highlightId={id}
				hideDetails={hidePeriodDetails}
				onRowPick={onPeriodRowPick}
				onRowDelete={onPeriodRowDelete}
				onRowPlay={handleRowPlay}
				onRowUpdate={onPeriodRowUpdate}
			/>
		</>
	);
};

type HighlightDetailsProps = Highlight & {
	title?: string;
	compact?: boolean;
	height?: string | number;
	sport?: string | null;
	hidePeriodDetails?: boolean | null;
	editorialPeriodPlayIds?: string[] | null;
	onPeriodRowPick?: (event: unknown, data: Period | Period[]) => void;
	onPeriodRowUpdate?: (newData: Period, oldData?: Period) => Promise<unknown>;
	onPeriodRowDelete?: (oldData: Period) => Promise<unknown>;
};

export default HighlightDetails;
